import { useState, useEffect } from 'react';
import { mockActivities } from '@/data/mockActivities';
import { Activity } from '@/types';

const ACTIVITIES_STORAGE_KEY = 'mock_activities';

export const useMockActivities = (projectId?: string) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId) {
      setIsLoading(false);
      return;
    }

    const timer = setTimeout(() => {
      try {
        const stored = localStorage.getItem(ACTIVITIES_STORAGE_KEY);
        let allActivities = stored ? JSON.parse(stored) : mockActivities;
        
        // Convert date strings back to Date objects
        allActivities = allActivities.map((a: any) => ({
          ...a,
          dueDate: new Date(a.dueDate),
          dateCompleted: a.dateCompleted ? new Date(a.dateCompleted) : null,
          createdAt: new Date(a.createdAt),
          updatedAt: new Date(a.updatedAt)
        }));

        const projectActivities = allActivities.filter((a: Activity) => a.projectId === projectId);
        setActivities(projectActivities);
        setIsLoading(false);
      } catch (err) {
        setError(err as Error);
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [projectId]);

  return { data: activities, isLoading, error, enabled: !!projectId };
};

export const useMockCreateActivity = () => {
  const [isLoading, setIsLoading] = useState(false);

  const mutateAsync = async (activityData: Partial<Activity> & { projectId: string }) => {
    setIsLoading(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const newActivity: Activity = {
        id: `activity-${Date.now()}`,
        name: activityData.name!,
        description: activityData.description || '',
        projectId: activityData.projectId,
        assignedTo: activityData.assignedTo || '',
        stage: activityData.stage!,
        status: activityData.status || 'not-started',
        isRequired: activityData.isRequired ?? true,
        dueDate: activityData.dueDate!,
        dateCompleted: null,
        comments: [],
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const stored = localStorage.getItem(ACTIVITIES_STORAGE_KEY);
      const currentActivities = stored ? JSON.parse(stored) : mockActivities;
      const updatedActivities = [...currentActivities, newActivity];
      localStorage.setItem(ACTIVITIES_STORAGE_KEY, JSON.stringify(updatedActivities));
      
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      throw err;
    }
  };

  return { mutate: mutateAsync, mutateAsync, isLoading };
};

export const useMockUpdateActivity = () => {
  const [isLoading, setIsLoading] = useState(false);

  const mutateAsync = async ({ id, projectId, ...updates }: Partial<Activity> & { id: string; projectId: string }) => {
    setIsLoading(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const stored = localStorage.getItem(ACTIVITIES_STORAGE_KEY);
      const currentActivities = stored ? JSON.parse(stored) : mockActivities;
      
      const updatedActivities = currentActivities.map((activity: Activity) => 
        activity.id === id 
          ? { ...activity, ...updates, updatedAt: new Date() }
          : activity
      );
      
      localStorage.setItem(ACTIVITIES_STORAGE_KEY, JSON.stringify(updatedActivities));
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      throw err;
    }
  };

  return { mutate: mutateAsync, mutateAsync, isLoading };
};